<template>
  <teleport to="body">
    <div id="huddle_add_people_dialog" class="fixed inset-0 z-[1001] flex items-center justify-center bg-black/40" @click="$emit('close')">
      <div
        class="w-[360px] max-h-[480px] flex flex-col rounded-xl shadow-xl overflow-hidden huddle_dialog_bg"
        @click.stop
      >
        <div class="flex items-center justify-between px-4 py-3 border_bottom">
          <h3 class="text-sm font-semibold text_color">Add people to huddle</h3>
          <button
            class="p-1 rounded transition-colors hover:bg-[var(--sidebar-item-hover)] secondary_color"
            @click="$emit('close')"
          >
            <ThemeImage :icon="imageNames.CLOSE_GREY" class="size-4" alt="close" />
          </button>
        </div>

        <div class="px-4 py-2">
          <CustomInput
            v-model="search"
            placeholder="Search people"
            type="primary"
            :icon="imageNames.SEARCH_VARIANT1"
            size="size-4"
            :auto-focus="true"
            @clear="search = ''"
          />
        </div>

        <div class="flex-1 overflow-y-auto px-2">
          <div v-if="filteredUsers.length === 0" class="text-center text-sm py-6 secondary_color">
            No users found
          </div>
          <button
            v-for="user in filteredUsers"
            :key="user.id"
            class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors"
            :class="isSelected(user.id) ? 'bg-blue-500/10' : 'hover:bg-[var(--sidebar-item-hover)]'"
            @click="toggleUser(user.id)"
          >
            <ProfileIcon :data="user" :size="28" :font="11" />
            <span class="flex-1 text-sm truncate text_color">
              {{ renderEntity(user) }}
            </span>
            <div
              v-if="isSelected(user.id)"
              class="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center"
            >
              <ThemeImage :icon="imageNames.TICK_WHITE" class="size-3" alt="selected" />
            </div>
          </button>
        </div>

        <div class="px-4 py-3 border_top flex items-center justify-between">
          <span class="text-xs secondary_color">
            {{ selected.length > 0 ? `${selected.length} selected` : 'Select people to invite' }}
          </span>
          <a-button
              class="primary_medium"
            :disabled="selected.length === 0 || sending"
            @click="handleInvite"
          >
            {{ sending ? 'Sending...' : 'Invite' }}
          </a-button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { renderEntity } from "@/src/utils/utils.js";

const emit = defineEmits(["close"]);
const store = useStore();

const search = ref("");
const selected = ref([]);
const sending = ref(false);


const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);
const participants = computed(() => store.getters["huddles/participants"]);

const participantIds = computed(() =>
  new Set([
    ...participants.value.map((p) => Number(p.user_id)),
    Number(currentUserId.value),
  ])
);

const availableUsers = computed(() =>
  allUsers.value.filter((u) => !participantIds.value.has(Number(u.id)))
);

const filteredUsers = computed(() => {
  if (!search.value) return availableUsers.value;
  const q = search.value.toLowerCase();
  return availableUsers.value.filter((u) => renderEntity(u).toLowerCase().includes(q));
});

const isSelected = (id) => selected.value.includes(id);

const toggleUser = (id) => {
  if (isSelected(id)) {
    selected.value = selected.value.filter((x) => x !== id);
  } else {
    selected.value = [...selected.value, id];
  }
};

const handleInvite = async () => {
  if (selected.value.length === 0) return;
  sending.value = true;
  try {
    await store.dispatch("huddles/inviteToHuddle", { userIds: selected.value });
    emit("close");
  } catch (error) {
    console.error("HuddleAddPeopleDialog: failed to invite", error);
  } finally {
    sending.value = false;
  }
};


</script>

<style scoped>
.huddle_dialog_bg {
  background: var(--background-color);
}
</style>
